import { useState, useEffect } from 'react';

interface MediaItem {
  id: string;
  type: 'image' | 'video';
  url: string;
  thumbnail?: string;
  caption?: string;
  size?: number;
  compressed?: boolean;
}

interface MediaGalleryProps {
  serviceTitle: string;
  onClose: () => void;
  isOpen?: boolean; // 선택적 prop으로 변경
}

// Cache Storage 및 localStorage 대체 방안
const CACHE_NAME = 'media-gallery-cache';
const ADMIN_PASSWORD_KEY = 'gallery-admin-auth';
const ADMIN_PASSWORD_STORAGE_KEY = 'gallery-admin-password';

// 기본 비밀번호
const DEFAULT_PASSWORD = '123456';

// 저장된 비밀번호 가져오기
function getStoredPassword(): string {
  try {
    const stored = localStorage.getItem(ADMIN_PASSWORD_STORAGE_KEY);
    return stored || DEFAULT_PASSWORD;
  } catch {
    return DEFAULT_PASSWORD;
  }
}

// 비밀번호 저장하기
function setStoredPassword(password: string): void {
  try {
    localStorage.setItem(ADMIN_PASSWORD_STORAGE_KEY, password);
  } catch (error) {
    console.error('비밀번호 저장 실패:', error);
  }
}

async function saveToCacheStorage(serviceId: string, items: MediaItem[]): Promise<void> {
  try {
    if ('caches' in window) {
      const cache = await caches.open(CACHE_NAME);
      const response = new Response(JSON.stringify({ items }), {
        headers: { 'Content-Type': 'application/json' }
      });
      await cache.put(`/media-${serviceId}`, response);
    } else {
      localStorage.setItem(`media-${serviceId}`, JSON.stringify(items));
    }
  } catch (error) {
    console.error('Cache Storage 저장 실패:', error);
    try {
      localStorage.setItem(`media-${serviceId}`, JSON.stringify(items));
    } catch (localError) {
      console.error('localStorage 저장도 실패:', localError);
    }
  }
}

async function loadFromCacheStorage(serviceId: string): Promise<MediaItem[]> {
  try {
    if ('caches' in window) {
      const cache = await caches.open(CACHE_NAME);
      const response = await cache.match(`/media-${serviceId}`);
      if (response) {
        const data = await response.json();
        return data.items || [];
      }
    }
    
    const stored = localStorage.getItem(`media-${serviceId}`);
    if (stored) {
      return JSON.parse(stored);
    }
    
    return [];
  } catch (error) {
    console.error('Cache Storage 로드 실패:', error);
    return [];
  }
}

function compressImage(file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (e) => {
      const img = new Image();
      img.src = e.target?.result as string;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx?.drawImage(img, 0, 0, width, height);

        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('압축 실패'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });
}

export function MediaGallery({ serviceTitle, onClose, isOpen = true }: MediaGalleryProps) {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);
  const [lightboxImage, setLightboxImage] = useState<MediaItem | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  
  // 비밀번호 변경 관련 상태
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // serviceTitle을 serviceId로 변환
  const validServiceId = serviceTitle && typeof serviceTitle === 'string' && serviceTitle.trim() !== '' 
    ? serviceTitle.trim() 
    : `default-${Date.now()}`;

  // 관리자 로그인 상태 확인
  useEffect(() => {
    const savedAuth = sessionStorage.getItem(ADMIN_PASSWORD_KEY);
    const storedPassword = getStoredPassword();
    if (savedAuth === storedPassword) {
      setIsAdmin(true);
    }
  }, []);

  // 초기 로드
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const items = await loadFromCacheStorage(validServiceId);
        setMediaItems(items);
      } catch (err) {
        console.error('데이터 로드 실패:', err);
        setError(`데이터를 불러오는데 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
      } finally {
        setIsLoading(false);
      }
    };
    
    if (isOpen) {
      loadData();
    }
  }, [validServiceId, isOpen]);

  // 저장
  useEffect(() => {
    if (mediaItems.length > 0 && !isLoading && isOpen) {
      saveToCacheStorage(validServiceId, mediaItems).catch(err => {
        console.error('저장 실패:', err);
        setError(`데이터 저장에 실패했습니다: ${err instanceof Error ? err.message : '알 수 없는 오류'}`);
      });
    }
  }, [mediaItems, validServiceId, isLoading, isOpen]);

  // 관리자 로그인
  const handleAdminLogin = () => {
    const storedPassword = getStoredPassword();
    if (adminPassword === storedPassword) {
      setIsAdmin(true);
      sessionStorage.setItem(ADMIN_PASSWORD_KEY, adminPassword);
      setShowAdminLogin(false);
      setAdminPassword('');
      setLoginError('');
    } else {
      setLoginError('비밀번호가 틀렸습니다.');
    }
  };

  // 관리자 로그아웃
  const handleAdminLogout = () => {
    setIsAdmin(false);
    sessionStorage.removeItem(ADMIN_PASSWORD_KEY);
  };

  // 비밀번호 변경
  const handlePasswordChange = () => {
    const storedPassword = getStoredPassword();
    
    if (currentPassword !== storedPassword) {
      setPasswordError('현재 비밀번호가 틀렸습니다.');
      return;
    }
    
    if (newPassword.length < 3) {
      setPasswordError('새 비밀번호는 3자리 이상이어야 합니다.');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setPasswordError('새 비밀번호와 확인 비밀번호가 일치하지 않습니다.');
      return;
    }
    
    if (newPassword === currentPassword) {
      setPasswordError('새 비밀번호는 현재 비밀번호와 달라야 합니다.');
      return;
    }
    
    // 비밀번호 변경
    setStoredPassword(newPassword);
    
    // 세션 업데이트
    sessionStorage.setItem(ADMIN_PASSWORD_KEY, newPassword);
    
    // 상태 초기화
    setShowPasswordChange(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    
    alert('비밀번호가 성공적으로 변경되었습니다.');
  };

  // 모달이 열려있지 않으면 렌더링하지 않음
  if (!isOpen) {
    return null;
  }

  async function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!isAdmin) return;
    
    const files = e.target.files;
    if (!files) return;

    const newItems: MediaItem[] = [];
    setError(null);

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');

      if (!isImage && !isVideo) continue;

      if (isImage && file.size > 10 * 1024 * 1024) {
        alert(`${file.name}은(는) 10MB를 초과합니다. 자동으로 압축됩니다.`);
      }
      if (isVideo && file.size > 50 * 1024 * 1024) {
        alert(`${file.name}은(는) 50MB를 초과하여 업로드할 수 없습니다.`);
        continue;
      }

      let finalBlob: Blob = file;
      let compressed = false;

      if (isImage && file.size > 1024 * 1024) {
        try {
          finalBlob = await compressImage(file);
          compressed = true;
        } catch (error) {
          console.error('압축 실패:', error);
          finalBlob = file;
        }
      }

      const url = URL.createObjectURL(finalBlob);
      const item: MediaItem = {
        id: `${Date.now()}-${i}-${Math.random().toString(36).substring(2)}`,
        type: isImage ? 'image' : 'video',
        url,
        caption: '',
        size: finalBlob.size,
        compressed,
      };

      newItems.push(item);
    }

    setMediaItems([...mediaItems, ...newItems]);
    e.target.value = '';
  }

  function handleDelete(id: string) {
    if (!isAdmin) return;
    setMediaItems(mediaItems.filter((item) => item.id !== id));
    if (lightboxImage?.id === id) {
      setLightboxImage(null);
    }
  }

  function handleEdit(item: MediaItem) {
    if (!isAdmin) return;
    setEditingId(item.id);
    setEditCaption(item.caption || '');
  }

  function handleSaveEdit() {
    if (!editingId || !isAdmin) return;
    setMediaItems(
      mediaItems.map((item) =>
        item.id === editingId ? { ...item, caption: editCaption } : item
      )
    );
    setEditingId(null);
    setEditCaption('');
  }

  function handleCancelEdit() {
    setEditingId(null);
    setEditCaption('');
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-800">{serviceTitle} 갤러리</h2>
          <div className="flex items-center gap-4">
            {isAdmin ? (
              <div className="flex items-center gap-3">
                <span className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full font-medium">
                  <i className="ri-shield-check-line mr-1"></i>
                  관리자 모드
                </span>
                <button
                  onClick={() => setShowPasswordChange(true)}
                  className="text-sm text-gray-500 hover:text-blue-600 transition-colors"
                  title="비밀번호 변경"
                >
                  <i className="ri-key-2-line"></i>
                </button>
                <button
                  onClick={handleAdminLogout}
                  className="text-sm text-gray-500 hover:text-red-600 transition-colors"
                  title="관리자 로그아웃"
                >
                  <i className="ri-logout-box-line"></i>
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowAdminLogin(true)}
                className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1 rounded-full transition-all"
              >
                <i className="ri-admin-line mr-1"></i>
                관리자
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl p-2 hover:bg-gray-100 rounded-full transition-all"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        </div>

        {/* 콘텐츠 */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
          <div className="flex items-center justify-between mb-6">
            <p className="text-gray-600">
              {isAdmin ? '사진과 동영상을 업로드하고 관리하세요' : '업로드된 사진과 동영상을 확인하세요'}
            </p>
            {isAdmin && (
              <label className="bg-[#0A7B83] hover:bg-[#0A7B83]/90 text-white px-6 py-3 rounded-xl cursor-pointer transition-all flex items-center gap-2 whitespace-nowrap font-medium">
                <i className="ri-upload-2-line"></i>
                파일 업로드
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-4 border-[#0A7B83] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-gray-500">갤러리를 불러오는 중...</p>
            </div>
          ) : mediaItems.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300">
              <i className="ri-image-add-line text-5xl text-gray-400 mb-3"></i>
              <p className="text-gray-500 text-lg font-medium mb-2">
                {isAdmin ? '아직 업로드된 파일이 없습니다' : '아직 등록된 갤러리가 없습니다'}
              </p>
              <p className="text-gray-400">
                {isAdmin ? '사진이나 동영상을 업로드해주세요' : '관리자가 사진을 등록하면 여기에 표시됩니다'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {mediaItems.map((item) => (
                <div
                  key={item.id}
                  className="relative group bg-gray-50 rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => setLightboxImage(item)}
                >
                  {item.type === 'image' ? (
                    <img
                      src={item.url}
                      alt={item.caption || '이미지'}
                      className="w-full h-48 object-cover object-top"
                    />
                  ) : (
                    <div className="relative w-full h-48 bg-black">
                      <video
                        src={item.url}
                        className="w-full h-full object-cover"
                        muted
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                        <i className="ri-play-circle-fill text-5xl text-white"></i>
                      </div>
                    </div>
                  )}

                  {item.compressed && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
                      압축됨
                    </div>
                  )}

                  {isAdmin && (
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                        }}
                        className="bg-blue-500 hover:bg-blue-600 text-white p-2.5 rounded-lg transition-all"
                        title="수정"
                      >
                        <i className="ri-edit-line"></i>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(item.id);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white p-2.5 rounded-lg transition-all"
                        title="삭제"
                      >
                        <i className="ri-delete-bin-line"></i>
                      </button>
                    </div>
                  )}

                  {item.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-2">
                      <p className="text-xs truncate">{item.caption}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 관리자 로그인 모달 */}
      {showAdminLogin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-800">관리자 로그인</h3>
            <p className="text-gray-600 mb-4 text-sm">갤러리 관리를 위해 비밀번호를 입력하세요</p>
            <p className="text-xs text-gray-500 mb-4">기본 비밀번호: 123456</p>
            <input
              type="password"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              placeholder="관리자 비밀번호"
              className="w-full border border-gray-300 rounded-xl p-4 mb-2 text-gray-700 focus:ring-2 focus:ring-[#0A7B83] focus:border-transparent outline-none"
              onKeyPress={(e) => e.key === 'Enter' && handleAdminLogin()}
            />
            {loginError && (
              <p className="text-red-500 text-sm mb-4">{loginError}</p>
            )}
            <div className="flex gap-3">
              <button
                onClick={handleAdminLogin}
                className="flex-1 bg-[#0A7B83] hover:bg-[#0A7B83]/90 text-white py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                로그인
              </button>
              <button
                onClick={() => {
                  setShowAdminLogin(false);
                  setAdminPassword('');
                  setLoginError('');
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 비밀번호 변경 모달 */}
      {showPasswordChange && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-800">비밀번호 변경</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">현재 비밀번호</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl p-4 text-gray-700 focus:ring-2 focus:ring-[#0A7B83] focus:border-transparent outline-none"
                  placeholder="현재 비밀번호를 입력하세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">새 비밀번호</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl p-4 text-gray-700 focus:ring-2 focus:ring-[#0A7B83] focus:border-transparent outline-none"
                  placeholder="새 비밀번호를 입력하세요"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">비밀번호 확인</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl p-4 text-gray-700 focus:ring-2 focus:ring-[#0A7B83] focus:border-transparent outline-none"
                  placeholder="새 비밀번호를 다시 입력하세요"
                />
              </div>
            </div>
            {passwordError && (
              <p className="text-red-500 text-sm mt-3">{passwordError}</p>
            )}
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePasswordChange}
                className="flex-1 bg-[#0A7B83] hover:bg-[#0A7B83]/90 text-white py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                변경
              </button>
              <button
                onClick={() => {
                  setShowPasswordChange(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmPassword('');
                  setPasswordError('');
                }}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 수정 모달 */}
      {editingId && isAdmin && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold mb-4 text-gray-800">설명 수정</h3>
            <textarea
              value={editCaption}
              onChange={(e) => setEditCaption(e.target.value)}
              placeholder="설명을 입력하세요..."
              className="w-full border border-gray-300 rounded-xl p-4 mb-4 resize-none text-gray-700 focus:ring-2 focus:ring-[#0A7B83] focus:border-transparent outline-none"
              rows={4}
            />
            <div className="flex gap-3">
              <button
                onClick={handleSaveEdit}
                className="flex-1 bg-[#0A7B83] hover:bg-[#0A7B83]/90 text-white py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                저장
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-3 rounded-xl font-medium transition-all whitespace-nowrap"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 라이트박스 */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-black/90 flex items-center justify-center z-[60] p-4"
          onClick={() => setLightboxImage(null)}
        >
          <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            {lightboxImage.type === 'image' ? (
              <img
                src={lightboxImage.url}
                alt={lightboxImage.caption || '원본 이미지'}
                className="w-full h-auto max-h-[80vh] object-contain rounded-xl"
              />
            ) : (
              <video
                src={lightboxImage.url}
                controls
                autoPlay
                className="w-full h-auto max-h-[80vh] rounded-xl"
              />
            )}

            {lightboxImage.caption && (
              <p className="text-white text-center mt-4 text-lg">
                {lightboxImage.caption}
              </p>
            )}

            <div className="flex gap-3 mt-6 justify-center">
              {isAdmin && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(lightboxImage);
                      setLightboxImage(null);
                    }}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-edit-line"></i>
                    수정
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(lightboxImage.id);
                      setLightboxImage(null);
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line"></i>
                    삭제
                  </button>
                </>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxImage(null);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 whitespace-nowrap"
              >
                <i className="ri-close-line"></i>
                종료
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
